import { useEffect, useMemo } from 'react';
import {
  useSelectedOrganizationId,
  useAuthtoken,
  logout,
} from 'stores/useAuthStore';
import { queryOptions, useQuery } from '@tanstack/react-query';

import request from 'utils/request';
import { showSnackbar , useMaskMode } from 'stores/useUiStore';
import { TOKEN_INVALID } from 'containers/Base/useBaseViewData';
import { generateFakeValue } from 'utils/mockFn';
import type { ApiRespBody } from 'types/api';

import type { Resource, ResourcesData } from '../resources.types';


export const RESOURCES_QUERY_KEY = ['resources'] as const;

export async function fetchResources(
  authToken: string,
  signal?: AbortSignal
): Promise<Resource[]> {
  const result = await request<ApiRespBody<Resource[]>>('/api/resources', {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', authorization: authToken },
    ...(signal ? { signal } : {}),
  });

  if (result?.success !== false) return result?.data ?? [];
  if (result.message === TOKEN_INVALID) throw new Error(TOKEN_INVALID);
  throw new Error(result.message || 'Failed to load resources');
}

export function resourcesQueryOptions(
  authToken: string,
  organizationId: number | string | null
) {
  return queryOptions({
    queryKey: [...RESOURCES_QUERY_KEY, organizationId],
    queryFn: ({ signal }) => fetchResources(authToken, signal),
    staleTime: Infinity,
    enabled: !!authToken,
  });
}

function maskResources(values: Resource[]): Resource[] {
  return values.map((r) => ({
    ...r,
    resourceID: generateFakeValue(r.resourceID, 'id'),
    ...(r.description != null
      ? { description: generateFakeValue(r.description, 'id') }
      : {}),
  }));
}

export type UseResourcesResult = ResourcesData & {
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
};

export function useResources(): UseResourcesResult {
  const authToken = useAuthtoken();
  const organizationId = useSelectedOrganizationId();
  const maskMode = useMaskMode();

  const query = useQuery(resourcesQueryOptions(authToken, organizationId));

  useEffect(() => {
    if (!(query.error instanceof Error)) return;
    if (query.error.message === TOKEN_INVALID) {
      logout();
    } else {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onResourcesGet',
          open: true,
          message: query.error.message,
          severity: 'error',
        });
    }
  }, [query.error]);

  const values = useMemo(() => {
    const data = query.data ?? [];
    return maskMode ? maskResources(data) : data;
  }, [query.data, maskMode]);

  const lastUpdated = useMemo(
    () =>
      query.dataUpdatedAt
        ? new Date(query.dataUpdatedAt).toISOString()
        : undefined,
    [query.dataUpdatedAt]
  );

  return useMemo(
    () => ({
      values,
      lastUpdated,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      refetch: query.refetch,
    }),
    [values, lastUpdated, query.isLoading, query.isFetching, query.refetch]
  );
}
